/**
 * Matriz de roles de staff de plataforma — espejo de UI (TASK-22).
 *
 * ⚠️ ESTO ES **SOLO UX**. Sirve para mostrar/ocultar pestañas y botones, y así
 * evitarle al operador clics que terminarían en un 403 predecible. NO es una
 * barrera de seguridad: el backend revalida CADA petición contra `auth_group`
 * en la base de datos y es la ÚNICA autoridad
 * (grade_card/apps/staff/permissions.py::HasStaffPermission).
 *
 * Fuente de verdad: `grade_card/apps/staff/groups.py::STAFF_MATRIX` (TASK-20 §4).
 * Esta copia es manual porque no hay artefacto del que derivarla: el cliente
 * generado (`openapi-typescript`) emite tipos, no datos de permisos. Si las dos
 * se desincronizan el peor caso es cosmético — un botón de más (fallará con 403
 * `staff_permission_denied`) o uno de menos (una función invisible).
 * `__tests__/permissions.test.js` fija esta matriz como especificación.
 *
 * Dos reglas que NO son obvias y que el backend aplica igual:
 *   1. Un recurso AUSENTE del dict de un rol = SIN acceso alguno a ese recurso
 *      (no "solo lectura"). Por eso SUPPORT no ve Facturas ni Métricas.
 *   2. Un usuario puede tener VARIOS roles ⇒ los permisos se UNEN.
 *
 * Verbos: los deriva el backend del método HTTP (GET→read, POST→create,
 * PUT/PATCH→update, DELETE→delete), salvo las @action que declaran el suyo en
 * `staff_action_verbs`:
 *   - academia suspend/reactivate  → verbo `suspend` (no `update`: SUPPORT puede
 *     suspender una academia pero no PATCHear su NIT ni su nombre).
 *   - suscripción confirm_payment  → verbo `update` (no `create`, aunque sea POST).
 *   - academia impersonate         → verbo propio `impersonate` (no `suspend`):
 *     asumir la identidad del owner es más sensible que un toggle operativo,
 *     así que solo PLATFORM_ADMIN lo tiene, ni siquiera SUPPORT.
 */

// ── Nombres de grupo (auth.Group.name), en orden de privilegio ───────────────
const PLATFORM_ADMIN = "PLATFORM_ADMIN";
const FINANCE = "FINANCE";
const SUPPORT = "SUPPORT";
const SALES = "SALES";
const READ_ONLY = "READ_ONLY";

const STAFF_MATRIX = {
  [PLATFORM_ADMIN]: {
    // "impersonate": ver la app como el owner de la academia (Evazard-style),
    // sin conocer/resetear su contraseña. Más restrictivo que `suspend` a
    // propósito — ni SUPPORT lo tiene: es asumir la identidad completa de
    // otra cuenta, no un toggle operativo.
    companies: ["read", "create", "update", "delete", "suspend", "impersonate"],
    subscriptions: ["read", "create", "update", "delete"],
    invoices: ["read"],
    metrics: ["read"],
    staff: ["read", "create", "update", "delete"],
    audit: ["read"],
  },
  [FINANCE]: {
    companies: ["read"],
    // "RW (plan/status)": escribir = cambiar plan/estado y confirmar pagos
    // (verbo `update`). Aprovisionar desde cero (POST) es PLATFORM_ADMIN.
    subscriptions: ["read", "update"],
    invoices: ["read"],
    metrics: ["read"], // payload recortado por el backend — ver METRICS_FIELDS_BY_ROLE
  },
  [SUPPORT]: {
    companies: ["read", "suspend"],
    subscriptions: ["read"],
    // invoices / metrics / audit: sin acceso (soporte no ve facturación).
  },
  [SALES]: {
    companies: ["read", "create"],
    subscriptions: ["read"],
    metrics: ["read"], // payload recortado a activación/conversión
  },
  [READ_ONLY]: {
    companies: ["read"],
    subscriptions: ["read"],
    invoices: ["read"],
    metrics: ["read"],
    audit: ["read"],
  },
};

/** Orden de mayor a menor privilegio (mismo que devuelve /users/me/). */
const STAFF_ROLES = [PLATFORM_ADMIN, FINANCE, SUPPORT, SALES, READ_ONLY];

/** Etiquetas en español para la UI (selects, badges). */
const STAFF_ROLE_LABELS = {
  [PLATFORM_ADMIN]: "Administrador de plataforma",
  [FINANCE]: "Finanzas",
  [SUPPORT]: "Soporte",
  [SALES]: "Comercial",
  [READ_ONLY]: "Solo lectura",
};

/**
 * Roles de staff efectivos del usuario de AuthProvider.
 *
 * Compartida por `useStaffPermissions` y `RequireSuperAdmin` para que la ruta y
 * la UI nunca discrepen.
 *
 *   - Array (incluido `[]`): valor real de /users/me/. `[]` = es staff pero SIN
 *     ningún grupo asignado; no debe ver nada (el layout muestra un aviso).
 *   - `undefined`/`null`: DESCONOCIDO. Ocurre en la ventana entre `login()` y la
 *     petición a /users/me/ (el JWT no emite `staff_roles`). Se degrada al
 *     comportamiento previo de TASK-18 —todo visible— para no dejar el panel en
 *     blanco; el backend sigue filtrando y AuthProvider cierra la ventana en una
 *     petición.
 */
function resolveStaffRoles(user) {
  const fromApi = user?.staff_roles;
  if (Array.isArray(fromApi)) return fromApi;
  if (user?.is_superuser || user?.is_staff || user?.role === "SUPERADMIN") {
    // is_superuser implica PLATFORM_ADMIN también en el backend (services.py).
    return [PLATFORM_ADMIN];
  }
  return [];
}

/**
 * ¿Alguno de `roles` puede `action` sobre `resource`? Función pura, sin React.
 * Sólo UX — ver la cabecera de este archivo.
 */
function canWithRoles(roles, resource, action) {
  if (!Array.isArray(roles)) return false;
  return roles.some(
    (role) => STAFF_MATRIX[role]?.[resource]?.includes(action) === true
  );
}

export {
  STAFF_MATRIX,
  STAFF_ROLES,
  STAFF_ROLE_LABELS,
  PLATFORM_ADMIN,
  FINANCE,
  SUPPORT,
  SALES,
  READ_ONLY,
  resolveStaffRoles,
  canWithRoles,
};
